import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Link from 'next/link';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import {
    ArrowRight,
    Users,
    GraduationCap,
    Clock,
    X,
    Globe,
    BookOpen,
    Heart,
    Award,
    Sparkles,
    Languages,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';
import { get } from 'utils/fetchRequest';
import { getCoverList } from 'context/actions/cover';
import { getActivePopup } from 'context/actions/popups';
import { getClubsList } from 'context/actions/clubs';
import { getPartnerSchools } from 'context/actions/partnerSchools';

// Scroll-triggered fade-in hook
const useScrollReveal = () => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.15 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    return [ref, visible];
};

const Section = ({ children, className = '' }) => {
    const [ref, visible] = useScrollReveal();
    return (
        <section
            ref={ref}
            className={`transition-all duration-1000 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            } ${className}`}
        >
            {children}
        </section>
    );
};

// ── PopupAd — нэг popup-ийн контентыг render хийнэ ─────────────
// X товч болон хаах footer нь PopupContainer-д байна (overflow-hidden
// асуудлаас зайлсхийхийн тулд).
const PopupAd = ({ popup, variant = 'primary' }) => {
    const reduxLang = useSelector((s) => s.system.lang || 'mn');
    const lang = i18n.language || reduxLang || 'mn';
    const isKo = lang === 'ko';
    const isEn = lang === 'en';

    const buttonText = isKo && popup.buttonTextKr ? popup.buttonTextKr : isEn && popup.buttonTextEn ? popup.buttonTextEn : popup.buttonText;
    const isSecondary = variant === 'secondary';
    const isExternal = popup.buttonUrl?.startsWith('http');

    if (popup.type === 'image') {
        const hasBtn = popup.hasButton && buttonText && popup.buttonUrl;
        return (
            <div className="shadow-2xl overflow-hidden rounded-lg bg-white">
                {/* Зураг — aspect ratio хадгалан бүтнээр харагдана.
                    height-ийг viewport-д тулгуурлан, width-ийг auto болгосноор
                    зураг өөрийн ratio-г бүрэн хадгалж, wrapper-т яг таарч
                    суурьшина (хажуу талд цагаан зай гарахгүй). */}
                <img
                    src={popup.imageUrl}
                    alt=""
                    style={{
                        display: 'block',
                        // Өндөр дээд хязгаартай, өргөн зургийн ratio-оор
                        maxHeight: 'min(55vh, 380px)',
                        maxWidth: '100%',
                        width: 'auto',
                        height: 'auto',
                    }}
                />
                {/* Товч — зөвхөн товч оруулсан үед л цагаан хэсэг */}
                {hasBtn && (
                    <div className="p-3 text-center border-t border-gray-100">
                        <a
                            href={popup.buttonUrl}
                            target={isExternal ? '_blank' : '_self'}
                            rel="noopener noreferrer"
                            className="inline-block bg-brand-yellow text-brand-navy font-bold px-5 py-2 text-sm hover:bg-yellow-300 transition rounded"
                        >
                            {buttonText.toUpperCase()}
                        </a>
                    </div>
                )}
            </div>
        );
    }

    // ── Текст төрөл ───────────────────────────────────────────
    const title = isKo && popup.titleKr ? popup.titleKr : isEn && popup.titleEn ? popup.titleEn : popup.title || '';
    const description = isKo && popup.descriptionKr ? popup.descriptionKr : isEn && popup.descriptionEn ? popup.descriptionEn : popup.description;

    return (
        <div
            className={`bg-brand-navy text-white ${isSecondary ? 'p-5' : 'p-6 sm:p-8'} text-center rounded-lg shadow-2xl`}
        >
            {title && (
                <h3
                    className={`${isSecondary ? 'text-xl' : 'text-2xl sm:text-3xl'} font-bold mb-3`}
                >
                    {title.toUpperCase()}
                </h3>
            )}
            {description && (
                <p className="text-white/80 text-sm mb-5 leading-relaxed">{description}</p>
            )}
            {popup.hasButton && buttonText && popup.buttonUrl && (
                <a
                    href={popup.buttonUrl}
                    target={isExternal ? '_blank' : '_self'}
                    rel="noopener noreferrer"
                    className={`inline-block bg-brand-yellow text-brand-navy font-bold ${isSecondary ? 'px-5 py-2 text-sm' : 'px-6 sm:px-8 py-2.5 sm:py-3'} hover:bg-yellow-300 transition`}
                >
                    {buttonText.toUpperCase()}
                </a>
            )}
        </div>
    );
};

// ── PopupCard — popup + X товч + хаах footer нэгтгэнэ ──────────
// X товч болон footer нь popup контентын ГАДНА байрлах учраас
// overflow-hidden тасалдахгүй.
// ── PartnersSection ──────────────────────────────────────────────────
const PartnersSection = ({ partners, t, lang }) => {
    const trackRef = useRef(null);
    const wrapperRef = useRef(null);
    const [needsMarquee, setNeedsMarquee] = React.useState(false);

    React.useEffect(() => {
        const check = () => {
            if (!trackRef.current || !wrapperRef.current) return;
            const trackW = trackRef.current.scrollWidth;
            const wrapW = wrapperRef.current.offsetWidth;
            setNeedsMarquee(trackW > wrapW);
        };
        check();
        const ro = new ResizeObserver(check);
        if (wrapperRef.current) ro.observe(wrapperRef.current);
        return () => ro.disconnect();
    }, [partners.length]);

    const duration = Math.max(20, partners.length * 4);

    const PartnerCard = ({ p }) => (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                padding: '0 28px',
                flexShrink: 0,
            }}
        >
            <div
                style={{
                    width: 120,
                    height: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#fff',
                    borderRadius: 16,
                    border: '1.5px solid #efefef',
                    padding: 12,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}
            >
                {p.logoUrl ? (
                    <img
                        src={p.logoUrl}
                        alt={p.name}
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                    />
                ) : (
                    <span style={{ fontSize: 40 }}>🏫</span>
                )}
            </div>
            <p
                style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#374151',
                    textAlign: 'center',
                    lineHeight: 1.4,
                    width: 120,
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                }}
            >
                {lang === 'ko' ? p.nameKr || p.name : p.name}
            </p>
        </div>
    );

    return (
        <section className="py-16 bg-white border-t border-gray-100">
            <div className="max-w-7xl mx-auto px-6 mb-10 text-center">
                <p className="text-xs font-bold tracking-[0.25em] uppercase text-gray-400 mb-2">
                    {t('home.partnersLabel')}
                </p>
                <h2 className="text-3xl md:text-4xl font-black text-brand-navy tracking-tight">
                    {t('home.partnersTitle')}
                </h2>
            </div>

            <div
                ref={wrapperRef}
                style={{ overflow: 'hidden', position: 'relative', padding: '8px 0' }}
            >
                {needsMarquee && (
                    <>
                        <div
                            style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: 100,
                                background: 'linear-gradient(to right, #fff, transparent)',
                                zIndex: 2,
                                pointerEvents: 'none',
                            }}
                        />
                        <div
                            style={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                bottom: 0,
                                width: 100,
                                background: 'linear-gradient(to left, #fff, transparent)',
                                zIndex: 2,
                                pointerEvents: 'none',
                            }}
                        />
                    </>
                )}
                <div
                    ref={trackRef}
                    style={{
                        display: 'flex',
                        flexWrap: 'nowrap',
                        width: needsMarquee ? 'max-content' : '100%',
                        justifyContent: needsMarquee ? 'flex-start' : 'center',
                        animation: needsMarquee
                            ? `partners-marquee ${duration}s linear infinite`
                            : 'none',
                    }}
                >
                    {(needsMarquee ? [...partners, ...partners, ...partners] : partners).map(
                        (p, i) => (
                            <PartnerCard key={i} p={p} />
                        )
                    )}
                </div>
            </div>

            <style>{`
                @keyframes partners-marquee {
                    0%   { transform: translateX(0); }
                    100% { transform: translateX(-33.333%); }
                }
                @media (prefers-reduced-motion: reduce) {
                    @keyframes partners-marquee { 0%, 100% { transform: none; } }
                }
            `}</style>
        </section>
    );
};

const PopupCard = ({ popup, onClose, variant = 'primary' }) => {
    const reduxLang = useSelector((s) => s.system.lang || 'mn');
    const lang = i18n.language || reduxLang || 'mn';
    const isKo = lang === 'ko';
    const isEn = lang === 'en';
    const isSecondary = variant === 'secondary';
    const isImage = popup.type === 'image';

    // ── Wrapper өргөн ────────────────────────────────────────
    // Зурагны хувьд: wrapper-д max width л тогтооно, зураг өөрийн ratio-р
    //   шалгагдаж, wrapper нь зурагны жинхэнэ өргөнд шахагдан орно
    //   (inline-flex + w-max трик). Ингэснээр X товч зургийн буланд таарна.
    // Текстийн хувьд: clamp-аар харьцангуй өргөн тогтооно.
    const wrapperStyle = isImage
        ? {
              // Image popup — зурагны ratio-оор шахагдана
              maxWidth: isSecondary ? 'clamp(130px, 44vw, 320px)' : 'clamp(150px, 44vw, 420px)',
          }
        : {
              width: isSecondary ? 'clamp(130px, 44vw, 300px)' : 'clamp(150px, 44vw, 400px)',
          };

    return (
        <div
            className="relative inline-flex flex-col"
            style={wrapperStyle}
            onClick={(e) => e.stopPropagation()}
        >
            {/* X товч — popup-ийн дээд баруун буланд, overflow-hidden-д таслагдахгүй */}
            <button
                type="button"
                onClick={() => onClose(false)}
                className="absolute -top-3 -right-3 z-20 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-gray-100 transition"
                aria-label="Хаах"
            >
                <X size={15} className="text-gray-700" />
            </button>

            {/* Popup контент */}
            <PopupAd popup={popup} variant={variant} />

            {/* Хаах товчнууд — popup-ийн доор */}
            <div className="flex items-center justify-center gap-1 mt-2 py-1">
                <button
                    type="button"
                    onClick={() => onClose(false)}
                    className="px-2 sm:px-3 py-1 text-xs text-white/80 hover:text-white transition rounded hover:bg-white/10 whitespace-nowrap"
                >
                    {isKo ? '닫기' : isEn ? 'Close' : 'Хаах'}
                </button>
                <span className="text-white/30 text-xs select-none">|</span>
                <button
                    type="button"
                    onClick={() => onClose(true)}
                    className="px-2 sm:px-3 py-1 text-xs text-white/80 hover:text-white transition rounded hover:bg-white/10 whitespace-nowrap"
                >
                    {isKo ? '다시 보지 않기' : isEn ? "Don't show again" : 'Дахин харагдахгүй'}
                </button>
            </div>
        </div>
    );
};

// ── PopupContainer ───────────────────────────────────────────────
const PopupContainer = ({ popups, onClose }) => {
    if (!popups || popups.length === 0) return null;

    // ── 1 popup — viewport-ын 90% хүртэл том ──
    if (popups.length === 1) {
        return (
            <div
                className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-6 animate-fade-in"
                onClick={() => onClose(false)}
            >
                <div style={{ width: 'min(90vw, 440px)' }}>
                    <PopupCard popup={popups[0]} onClose={onClose} variant="primary" />
                </div>
            </div>
        );
    }

    // ── 2 popup ─────────────────────────────────────────────
    // PopupCard-ийн өргөн `clamp(130px, 44vw, 300/400px)` тул
    // 2 карт + gap нийт нь viewport өргөнд байнга багтана.
    // gap: mobile-д 8px (2vw), desktop-д 24px
    const [primary, secondary] = popups;
    return (
        <div
            className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center animate-fade-in"
            style={{ padding: '20px 12px' }}
            onClick={() => onClose(false)}
        >
            <div
                className="flex items-start justify-center"
                style={{ gap: 'clamp(8px, 2vw, 24px)' }}
                onClick={(e) => e.stopPropagation()}
            >
                <PopupCard popup={primary} onClose={onClose} variant="primary" />
                <PopupCard popup={secondary} onClose={onClose} variant="secondary" />
            </div>
        </div>
    );
};

// ══════════════════════════════════════════════════════════════
// DYNAMIC HERO — зураг + видео slideshow
// ══════════════════════════════════════════════════════════════
const DynamicHero = () => {
    const [covers, setCovers] = useState([]);
    const [current, setCurrent] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [firstReady, setFirstReady] = useState(false); // эхний cover бэлэн болсон эсэх

    // ref-ууд — render-д нөлөөлөхгүй утгуудыг ref-д хадгална
    const timerRef = useRef(null);
    const videoRefs = useRef({}); // { [cover.id]: HTMLVideoElement }
    const coversRef = useRef([]); // covers-ийн ref хувилбар — stale closure-оос хамгаалах
    const currentRef = useRef(0); // current-ийн ref хувилбар

    // covers болон current өөрчлөгдөхөд ref-ийг шинэчлэх
    useEffect(() => {
        coversRef.current = covers;
    }, [covers]);
    useEffect(() => {
        currentRef.current = current;
    }, [current]);

    // ── Cover жагсаалт ачаалах — алдаа гарвал retry ──────────────────
    useEffect(() => {
        let cancelled = false;
        const load = async (attempt = 0) => {
            try {
                const res = await getCoverList('', 1, 50);
                if (cancelled) return;
                const list = res?.data?.list || res?.data || [];
                setCovers(Array.isArray(list) ? list : []);
            } catch {
                if (cancelled) return;
                if (attempt < 2) setTimeout(() => load(attempt + 1), 2000);
            } finally {
                if (!cancelled) setLoaded(true);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, []);

    // ── Зураг автомат шилжих timer ───────────────────────────────────
    const startTimer = useCallback((idx, items) => {
        clearTimeout(timerRef.current);
        if (!items || items.length < 2) return;
        if (items[idx]?.isVideo) return; // видео onEnded-д найдна
        timerRef.current = setTimeout(() => {
            const next = (idx + 1) % items.length;
            setCurrent(next);
            startTimer(next, items);
        }, 5000);
    }, []);

    useEffect(() => {
        if (covers.length > 0) startTimer(0, covers);
        return () => clearTimeout(timerRef.current);
    }, [covers, startTimer]);

    // ── Slide шилжих ─────────────────────────────────────────────────
    const goTo = useCallback(
        (i) => {
            clearTimeout(timerRef.current);
            setCurrent(i);
            startTimer(i, coversRef.current);
        },
        [startTimer]
    );

    const prev = useCallback(() => {
        const n = coversRef.current.length;
        goTo((currentRef.current - 1 + n) % n);
    }, [goTo]);

    const next = useCallback(() => {
        const n = coversRef.current.length;
        goTo((currentRef.current + 1) % n);
    }, [goTo]);

    // ── Видео play / pause — canplay хүлээгээд тоглуулна ─────────────
    useEffect(() => {
        const cleanups = [];

        covers.forEach((item, i) => {
            if (!item.isVideo) return;
            const vid = videoRefs.current[item.id];
            if (!vid) return;

            if (i === current) {
                const needsLoad = !vid.src || !vid.src.includes(item.imageUrl);

                const doPlay = () => {
                    vid.currentTime = 0;
                    vid.play().catch(() => {});
                };

                if (needsLoad) {
                    // canplay listener — нэг удаа дуусаад цэвэрлэгдэнэ
                    const onCanPlay = () => doPlay();
                    vid.addEventListener('canplay', onCanPlay, { once: true });
                    // useEffect cleanup-д listener-ийг бүртгэж хэвээр арилгана
                    cleanups.push(() => vid.removeEventListener('canplay', onCanPlay));

                    vid.src = item.imageUrl;
                    vid.load();
                } else {
                    doPlay();
                }
            } else {
                // Идэвхгүй видео — src арилгаж санах ой чөлөөлнө
                vid.pause();
                vid.src = '';
                vid.load();
            }
        });

        // cleanup: current өөрчлөгдөхөд хуучин canplay listener арилгана
        return () => cleanups.forEach((fn) => fn());
    }, [current, covers]);

    // ── Fallback — cover байхгүй ──────────────────────────────────────
    if (loaded && covers.length === 0) {
        return (
            <section className="relative w-full h-screen overflow-hidden">
                <Image
                    src="/homeCover.jpg"
                    alt="Хүрээ Тулга сургууль"
                    fill
                    className="object-cover"
                    priority
                />
            </section>
        );
    }

    // ── Slideshow ─────────────────────────────────────────────────────
    return (
        <section className="relative w-full h-screen overflow-hidden">
            {/* Loading overlay — эхний cover бэлэн болоход DOM-оос устана */}
            {!firstReady && <div className="absolute inset-0 z-10 pointer-events-none" />}

            {covers.map((item, i) => (
                <div
                    key={item.id}
                    className={`absolute inset-0 transition-opacity duration-1000 ${
                        i === current ? 'opacity-100 z-[1]' : 'opacity-0 z-0'
                    }`}
                >
                    {item.isVideo ? (
                        <video
                            ref={(el) => {
                                videoRefs.current[item.id] = el;
                            }}
                            preload="metadata"
                            muted
                            playsInline
                            disablePictureInPicture
                            controlsList="nodownload nofullscreen noremoteplayback"
                            loop={covers.length === 1}
                            onEnded={() => goTo((i + 1) % covers.length)}
                            onCanPlay={i === 0 ? () => setFirstReady(true) : undefined}
                            className="w-full h-full object-cover"
                            style={{ pointerEvents: 'none' }}
                        />
                    ) : (
                        <img
                            src={item.imageUrl}
                            alt=""
                            onLoad={i === 0 ? () => setFirstReady(true) : undefined}
                            fetchPriority={i === 0 ? 'high' : 'low'}
                            className="w-full h-full object-cover"
                        />
                    )}
                </div>
            ))}

            {/* Шилжих товч */}
            {covers.length > 1 && (
                <>
                    <button
                        onClick={prev}
                        className="absolute left-6 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 text-white rounded-full p-3 transition"
                        aria-label="Өмнөх"
                    >
                        <ChevronLeft size={24} />
                    </button>
                    <button
                        onClick={next}
                        className="absolute right-6 top-1/2 -translate-y-1/2 z-20 bg-white/20 hover:bg-white/40 text-white rounded-full p-3 transition"
                        aria-label="Дараах"
                    >
                        <ChevronRight size={24} />
                    </button>

                    {/* Dots */}
                    <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                        {covers.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => goTo(i)}
                                className={`rounded-full transition-all ${
                                    i === current
                                        ? 'bg-white w-6 h-2'
                                        : 'bg-white/50 w-2 h-2 hover:bg-white/80'
                                }`}
                                aria-label={`${i + 1}-р слайд`}
                            />
                        ))}
                    </div>
                </>
            )}
        </section>
    );
};

const HomePage = () => {
    const { t } = useTranslation();
    const reduxLang = useSelector((s) => s.system.lang || 'mn');
    const lang = i18n.language || reduxLang || 'mn';
    const [activePopups, setActivePopups] = useState([]);
    const [showPopup, setShowPopup] = useState(false);
    const [news, setNews] = useState([]);
    const [clubs, setClubs] = useState([]);
    const [partners, setPartners] = useState([]);

    useEffect(() => {
        const seen = sessionStorage.getItem('popup_seen');
        if (seen) return;

        let timer;
        getActivePopup()
            .then((res) => {
                const data = res?.data;
                // Backend одоо list буцаах ([ { id, type, ... }, ... ])
                // хуучин нэг object буцаах хувилбарыг ч дэмжинэ
                let popups = [];
                if (Array.isArray(data)) popups = data;
                else if (data && typeof data === 'object' && data.id) popups = [data];

                if (popups.length > 0) {
                    setActivePopups(popups.slice(0, 2)); // хамгийн ихдээ 2
                    timer = setTimeout(() => setShowPopup(true), 1500);
                }
            })
            .catch(() => {});

        return () => clearTimeout(timer);
    }, []);

    const closePopup = (permanent) => {
        setShowPopup(false);
        if (permanent) {
            // "Дахин харагдахгүй" — session дуустал харуулахгүй
            sessionStorage.setItem('popup_seen', '1');
        }
        // permanent=false бол sessionStorage хөндөхгүй → reload-д дахин гарна
    };

    useEffect(() => {
        (async () => {
            try {
                const result = await get('news/client?limit=6');
                if (result?.data) setNews(result.data);
            } catch (err) {
                console.error(err);
            }
        })();
    }, []);

    // ── Clubs ачаалах (home page-ийн section-д ашиглагдана) ──
    useEffect(() => {
        (async () => {
            try {
                const res = await getClubsList();
                if (res?.status === 'success' || res?.code === 200) {
                    setClubs(res.data || []);
                }
            } catch (err) {
                console.error(err);
            }
        })();
    }, []);

    // ── Хамтрагч сургуулиуд ──
    useEffect(() => {
        (async () => {
            try {
                const res = await getPartnerSchools();
                if (res?.status === 'success' || res?.code === 200) {
                    setPartners(res.data || []);
                }
            } catch (err) {
                console.error(err);
            }
        })();
    }, []);

    return (
        <>
            {showPopup && activePopups.length > 0 && (
                <PopupContainer popups={activePopups} onClose={closePopup} />
            )}

            {/* ── HERO ─────────────────────────────────────────── */}
            <DynamicHero />

            {/* ── STATS ───────────────────────────────────────── */}
            <Section className="bg-gray-50 py-24">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                        <div>
                            <Users
                                size={48}
                                className="mx-auto text-brand-navy mb-4"
                                strokeWidth={1.5}
                            />
                            <div className="text-6xl md:text-7xl font-black text-brand-navy mb-2">
                                20:1
                            </div>
                            <div className="text-sm tracking-[0.2em] text-gray-600">
                                {t('stats.studentFaculty')}
                            </div>
                        </div>
                        <div>
                            <GraduationCap
                                size={48}
                                className="mx-auto text-brand-navy mb-4"
                                strokeWidth={1.5}
                            />
                            <div className="text-6xl md:text-7xl font-black text-brand-navy mb-2">
                                500+
                            </div>
                            <div className="text-sm tracking-[0.2em] text-gray-600">
                                {t('stats.graduates')}
                            </div>
                        </div>
                        <div>
                            <Clock
                                size={48}
                                className="mx-auto text-brand-navy mb-4"
                                strokeWidth={1.5}
                            />
                            <div className="text-6xl md:text-7xl font-black text-brand-navy mb-2">
                                20+
                            </div>
                            <div className="text-sm tracking-[0.2em] text-gray-600">
                                {t('stats.years')}
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            {/* ── ABOUT ──────────────────────────────────────── */}
            <Section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
                        <div>
                            <h2 className="text-5xl md:text-6xl font-black text-brand-navy tracking-tight">
                                {t('home.aboutTitle')}
                            </h2>
                        </div>
                        <div className="lg:col-span-2 space-y-6 text-gray-700 leading-relaxed text-lg">
                            <p>{t('home.aboutDescription')}</p>
                            <p className="text-brand-navy font-bold text-xl italic">
                                {t('home.motto')}
                            </p>
                            <Link
                                href="/content/59"
                                className="inline-flex items-center gap-2 text-brand-red font-bold hover:gap-4 transition-all"
                            >
                                {t('common.readMore')}
                                <ArrowRight size={18} />
                            </Link>
                        </div>
                    </div>
                </div>
            </Section>

            {/* ── ADVANTAGES (Бидний давуу тал) ─────────────── */}
            <Section className="py-24 bg-brand-navy relative overflow-hidden">
                {/* Decorative background */}
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-10 pointer-events-none"
                    style={{
                        backgroundImage:
                            'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.35) 1px, transparent 0)',
                        backgroundSize: '32px 32px',
                    }}
                />
                <div
                    aria-hidden
                    className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-brand-red/20 blur-3xl"
                />
                <div
                    aria-hidden
                    className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-brand-yellow/20 blur-3xl"
                />

                <div className="max-w-7xl mx-auto px-6 relative">
                    {/* Heading */}
                    <div className="max-w-3xl mb-16">
                        <div className="inline-flex items-center gap-2 text-brand-yellow font-bold text-sm tracking-[0.3em] uppercase mb-4">
                            <Sparkles size={16} />
                            {t('home.advantagesSubtitle')}
                        </div>
                        <h2 className="text-5xl md:text-6xl font-black text-white tracking-tight">
                            {t('home.advantagesTitle')}
                        </h2>
                    </div>

                    {/* 6 Advantage Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            {
                                icon: Languages,
                                titleKey: 'advantages.bilingual_title',
                                descKey: 'advantages.bilingual_desc',
                                accent: 'bg-brand-yellow text-brand-navy',
                            },
                            {
                                icon: Users,
                                titleKey: 'advantages.smallClass_title',
                                descKey: 'advantages.smallClass_desc',
                                accent: 'bg-brand-red text-white',
                            },
                            {
                                icon: Globe,
                                titleKey: 'advantages.international_title',
                                descKey: 'advantages.international_desc',
                                accent: 'bg-brand-yellow text-brand-navy',
                            },
                            {
                                icon: Heart,
                                titleKey: 'advantages.values_title',
                                descKey: 'advantages.values_desc',
                                accent: 'bg-brand-red text-white',
                            },
                            {
                                icon: BookOpen,
                                titleKey: 'advantages.afterSchool_title',
                                descKey: 'advantages.afterSchool_desc',
                                accent: 'bg-brand-yellow text-brand-navy',
                            },
                            {
                                icon: Award,
                                titleKey: 'advantages.results_title',
                                descKey: 'advantages.results_desc',
                                accent: 'bg-brand-red text-white',
                            },
                        ].map((item, i) => {
                            const Icon = item.icon;
                            return (
                                <div
                                    key={i}
                                    className="group relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-sm p-8 hover:bg-white/10 hover:border-white/20 transition-all duration-300"
                                >
                                    {/* Number badge */}
                                    <div className="absolute top-6 right-6 text-6xl font-black text-white/5 group-hover:text-white/10 transition">
                                        0{i + 1}
                                    </div>

                                    {/* Icon */}
                                    <div
                                        className={`inline-flex items-center justify-center w-14 h-14 rounded-sm mb-6 ${item.accent} group-hover:scale-110 transition-transform`}
                                    >
                                        <Icon size={26} strokeWidth={2.5} />
                                    </div>

                                    {/* Title */}
                                    <h3 className="text-xl font-bold text-white mb-3 leading-tight">
                                        {t(item.titleKey)}
                                    </h3>

                                    {/* Description */}
                                    <p className="text-white/70 text-sm leading-relaxed">
                                        {t(item.descKey)}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Section>

            {/* ── ACADEMICS (3 level cards) ─────────────────── */}
            <Section className="py-24 bg-gray-50">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-5xl md:text-6xl font-black text-brand-navy tracking-tight mb-16">
                        {t('home.academicsTitle').toUpperCase()}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            {
                                color: 'bg-brand-red',
                                titleKey: 'grades.primary_name',
                                rangeKey: 'grades.primary_range',
                                descKey: 'grades.primary_desc',
                            },
                            {
                                color: 'bg-brand-navy',
                                titleKey: 'grades.middle_name',
                                rangeKey: 'grades.middle_range',
                                descKey: 'grades.middle_desc',
                            },
                            {
                                color: 'bg-brand-yellow',
                                titleKey: 'grades.high_name',
                                rangeKey: 'grades.high_range',
                                descKey: 'grades.high_desc',
                            },
                        ].map((item, idx) => (
                            <div
                                key={idx}
                                className="group cursor-pointer bg-white overflow-hidden hover:shadow-xl transition-all duration-300"
                            >
                                <div className={`${item.color} h-48 flex items-end p-6 text-white`}>
                                    <div>
                                        <div className="text-xs tracking-[0.2em] opacity-80 mb-1">
                                            {t('home.gradeLabel').toUpperCase()} {t(item.rangeKey)}
                                        </div>
                                        <div className="text-3xl font-black">
                                            {t(item.titleKey)}
                                        </div>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <p className="text-gray-700 leading-relaxed">
                                        {t(item.descKey)}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* ── CLUBS (Дугуйлан) ─────────────────────────── */}
            {clubs.length > 0 && (
                <Section className="py-24 bg-white relative overflow-hidden">
                    {/* Decorative accents */}
                    <div className="absolute top-0 right-0 w-2 h-40 bg-brand-red" />
                    <div className="absolute bottom-0 left-0 w-40 h-2 bg-brand-yellow" />

                    <div className="max-w-7xl mx-auto px-6 relative">
                        {/* Header */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-end mb-16">
                            <div>
                                <div className="inline-flex items-center gap-2 text-xs tracking-[0.25em] text-brand-red font-bold uppercase mb-4">
                                    <span className="w-8 h-[2px] bg-brand-red" />
                                    {t('home.clubsSubtitle')}
                                </div>
                                <h2 className="text-5xl md:text-6xl font-black text-brand-navy tracking-tight leading-none">
                                    {t('home.clubsTitle')}
                                </h2>
                            </div>
                            <div>
                                <p className="text-gray-600 text-lg leading-relaxed">
                                    {t('home.clubsDescription')}
                                </p>
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-2 gap-6 mb-16 max-w-2xl">
                            <div className="border-l-4 border-brand-yellow pl-6">
                                <div className="text-5xl md:text-6xl font-black text-brand-navy leading-none mb-2">
                                    {clubs.length}
                                </div>
                                <div className="text-xs tracking-[0.2em] text-gray-600 uppercase font-semibold">
                                    {t('home.clubsCount')}
                                </div>
                            </div>
                            <div className="border-l-4 border-brand-red pl-6">
                                <div className="text-5xl md:text-6xl font-black text-brand-navy leading-none mb-2">
                                    {clubs.reduce((sum, c) => sum + (c.teachers?.length || 0), 0)}
                                </div>
                                <div className="text-xs tracking-[0.2em] text-gray-600 uppercase font-semibold">
                                    {t('home.clubsTeachers')}
                                </div>
                            </div>
                        </div>

                        {/* Preview grid — хамгийн ихдээ 6 дугуйлан */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 mb-10">
                            {clubs.slice(0, 6).map((club) => (
                                <Link
                                    key={club.id}
                                    href={`/clubs/${club.id}`}
                                    className="group relative aspect-square rounded-lg overflow-hidden bg-gray-100 shadow-sm hover:shadow-xl transition-all"
                                >
                                    {club.coverImageUrl ? (
                                        <Image
                                            src={club.coverImageUrl}
                                            alt={club.name || ''}
                                            fill
                                            className="object-cover group-hover:scale-110 transition-transform duration-500"
                                            sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 16vw"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-300 text-3xl">
                                            ⬡
                                        </div>
                                    )}
                                    {/* Gradient overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/40 to-transparent opacity-80 group-hover:opacity-95 transition-opacity" />
                                    {/* Name at bottom */}
                                    <div className="absolute inset-x-0 bottom-0 p-3">
                                        <div className="text-white text-xs md:text-sm font-bold leading-tight line-clamp-2">
                                            {club.name}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>

                        {/* CTA button */}
                        <div className="flex justify-center">
                            <Link
                                href="/clubs"
                                className="inline-flex items-center gap-2 bg-brand-navy text-white font-bold px-8 py-3.5 hover:bg-brand-red transition-colors group"
                            >
                                {t('home.clubsViewAll')}
                                <ArrowRight
                                    size={16}
                                    className="group-hover:translate-x-1 transition-transform"
                                />
                            </Link>
                        </div>
                    </div>
                </Section>
            )}

            {/* ── NEWS ──────────────────────────────────────── */}
            {news.length > 0 && (
                <Section className="py-24 bg-gray-50">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="flex items-end justify-between mb-12">
                            <h2 className="text-5xl md:text-6xl font-black text-brand-navy tracking-tight">
                                {t('home.newsTitle')}
                            </h2>
                            <Link
                                href="/news"
                                className="text-brand-red font-bold hidden md:inline-flex items-center gap-2 hover:gap-4 transition-all"
                            >
                                {t('common.viewAll')}
                                <ArrowRight size={18} />
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {news.slice(0, 6).map((item) => (
                                <Link
                                    key={item.id}
                                    href={`/news/${item.id}`}
                                    className="group bg-gray-50 overflow-hidden hover:shadow-xl transition-all"
                                >
                                    <div className="relative aspect-[4/3] bg-gray-200 overflow-hidden">
                                        {item.imageUrl && (
                                            <Image
                                                src={item.imageUrl}
                                                alt={item.name || ''}
                                                fill
                                                className="object-cover group-hover:scale-105 transition-transform duration-100"
                                                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                            />
                                        )}
                                    </div>
                                    <div className="p-6">
                                        <h3 className="text-xl font-bold text-brand-navy mb-2 line-clamp-2 group-hover:text-brand-red transition">
                                            {item.name}
                                        </h3>
                                        <p className="text-gray-600 text-sm line-clamp-2">
                                            {item.description}
                                        </p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </Section>
            )}
            {/* ── ХАМТРАГЧ СУРГУУЛИУД ──────────────────────── */}
            {partners.length > 0 && <PartnersSection partners={partners} t={t} lang={lang} />}
        </>
    );
};

export default HomePage;